import React, { useEffect, useState } from "react";
import axios from 'axios';
import moment from 'moment';
import { Pencil, Trash2, CircleOff } from "lucide-react";

export default function TransactionHistory() {
    const [students, setStudents] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [transactionRecords, setTransactionRecords] = useState([]);
    const [transactionPage, setTransactionPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const StudentsPerPage = 16;
    const TransactionsPerPage = 16;

    useEffect(() => {
        const fetchStudents = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getStudent`);
                setStudents(response.data.data);
            } catch (error) {
                console.error('Error fetching students:', error);
            }
        };
        fetchStudents();
    }, []);

    const fetchTransactionRecords = async (student_id) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getStudentTransactionRecords/${student_id}`);
            setTransactionRecords(response.data.data);
            setTransactionPage(1); // Reset to page 1 when opening modal
        } catch (error) {
            console.error('Error fetching transaction records:', error);
            setTransactionRecords([]);
        }
    };

    const filteredStudents = students.filter((s) => {
        const matchesSearch = `${s.student_id} ${s.first_name} ${s.surname} ${s.chinese_name} ${s.school}`
            .toLowerCase()
            .includes(searchQuery.toLowerCase());
        return matchesSearch;
    });

    const indexOfLastStudent = currentPage * StudentsPerPage;
    const indexOfFirstStudent = indexOfLastStudent - StudentsPerPage;
    const currentStudents = filteredStudents.slice(indexOfFirstStudent, indexOfLastStudent);
    const totalStudentPages = Math.ceil(filteredStudents.length / StudentsPerPage);

    const indexOfLastTransaction = transactionPage * TransactionsPerPage;
    const indexOfFirstTransaction = indexOfLastTransaction - TransactionsPerPage;
    const currentTransactions = transactionRecords.slice(indexOfFirstTransaction, indexOfLastTransaction);
    const totalTransactionPages = Math.ceil(transactionRecords.length / TransactionsPerPage);

    const handleOpenStudentModal = (student) => {
        setSelectedStudent(student);
        fetchTransactionRecords(student.student_id);
    };

    const handleCloseModal = () => {
        setSelectedStudent(null);
        setTransactionRecords([]);
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value.toLowerCase());
        setCurrentPage(1);
    };

    return (
        <div className="p-8 min-h-screen overflow-y-auto">
            <h1 className="text-2xl font-bold">Transaction History</h1>
            <div className="mt-2 mb-4 border-t border-gray-400 w-9/12 mx-auto"></div>

            {/* Search Bar */}
            <div className="mb-4 flex flex-col md:flex-row md:items-center md:gap-4">
                <input
                    type="text"
                    placeholder="Search by name, ID"
                    value={searchQuery}
                    onChange={handleSearch}
                    className="w-[300px] px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
            </div>

            {/* Student Table */}
            <table className="w-full border-collapse bg-white shadow rounded">
                <thead>
                    <tr className="bg-gray-100">
                        <th className="border px-4 py-2">Student ID</th>
                        <th className="border px-4 py-2">Surname</th>
                        <th className="border px-4 py-2">First Name</th>
                        <th className="border px-4 py-2">Chinese Name</th>
                        <th className="border px-4 py-2">Form</th>
                        <th className="border px-4 py-2">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {currentStudents.map(student => (
                        <tr key={student.student_id} className="text-center border-t">
                            <td className="border px-4 py-2">{student.student_id}</td>
                            <td className="border px-4 py-2">{student.surname}</td>
                            <td className="border px-4 py-2">{student.first_name}</td>
                            <td className="border px-4 py-2">{student.chinese_name || '-'}</td>
                            <td className="border px-4 py-2">{student.grade || '-'}</td>
                            <td className="border px-4 py-2">
                                <div className="flex items-center justify-center">
                                    <button
                                        onClick={() => handleOpenStudentModal(student)}
                                    >
                                        <Pencil className="w-5 h-5 text-blue-600 hover:text-blue-800" />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Pagination Controls for Students */}
            <div className="flex justify-between items-center mt-6">
                <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    className="px-3 py-1 bg-gray-300 text-gray-800 rounded disabled:opacity-50"
                    disabled={currentPage === 1}
                >
                    Previous
                </button>
                <span className="text-sm">
                    Page {currentPage} of {totalStudentPages}
                </span>
                <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalStudentPages))}
                    className="px-3 py-1 bg-gray-300 text-gray-800 rounded disabled:opacity-50"
                    disabled={currentPage === totalStudentPages}
                >
                    Next
                </button>
            </div>

            {/* Student Transaction Record Modal */}
            {selectedStudent && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
                        <h2 className="text-xl font-bold mb-4">
                            Transaction History for {selectedStudent.surname} {selectedStudent.first_name} (ID: {selectedStudent.student_id})
                        </h2>
                        <table className="w-full border-collapse bg-white shadow rounded">
                            <thead>
                                <tr className="bg-gray-100">
                                    <th className="border px-4 py-2">Token Name</th>
                                    <th className="border px-4 py-2">Transaction Type</th>
                                    <th className="border px-4 py-2">Detail</th>
                                    <th className="border px-4 py-2">Amount</th>
                                    <th className="border px-4 py-2">Timestamp</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentTransactions.length > 0 ? (
                                    currentTransactions.map(record => (
                                        <tr key={`${record.student_id}-${record.timestamp}`} className="text-center border-t">
                                            <td className="border px-4 py-2">{record.token_name}</td>
                                            <td className="border px-4 py-2">{record.transaction_type}</td>
                                            <td className="border px-4 py-2">{record.transaction_detail}</td>
                                            <td className="border px-4 py-2">{record.amount}</td>
                                            <td className="border px-4 py-2">
                                                {moment(record.timestamp).format('YYYY-MM-DD HH:mm:ss')}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="border px-4 py-2 text-center">
                                            No transaction records found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        {/* Pagination Controls for Transactions */}
                        <div className="flex justify-between items-center mt-4">
                            <button
                                onClick={() => setTransactionPage(prev => Math.max(prev - 1, 1))}
                                className="px-3 py-1 bg-gray-300 text-gray-800 rounded disabled:opacity-50"
                                disabled={transactionPage === 1}
                            >
                                Previous
                            </button>
                            <span className="text-sm">
                                Page {transactionPage} of {totalTransactionPages}
                            </span>
                            <button
                                onClick={() => setTransactionPage(prev => Math.min(prev + 1, totalTransactionPages))}
                                className="px-3 py-1 bg-gray-300 text-gray-800 rounded disabled:opacity-50"
                                disabled={transactionPage === totalTransactionPages}
                            >
                                Next
                            </button>
                        </div>
                        <button
                            onClick={handleCloseModal}
                            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}